/**
 * useAnalytics Hook
 * 
 * Provides analytics tracking functionality for client-side components.
 * Sends events to the backend API which forwards to Watchtower.
 */

import { useCallback } from "react";
import { collectData } from "@caresource/watchtower-js";
import { getCsrfToken } from "../utils/csrfToken";

/**
 * Analytics event names for Member Info Viewer
 */
export const AnalyticsEvents = {
    // Pre-login events
    FIELD_ENTRY_FIRST_NAME: "miv_field_entry_first_name",
    FIELD_ENTRY_LAST_NAME: "miv_field_entry_last_name",
    FIELD_ENTRY_DOB: "miv_field_entry_dob",
    FIELD_ENTRY_MEMBER_ID: "miv_field_entry_member_id",
    FIELD_ENTRY_SSN: "miv_field_entry_ssn",
    SSN_BACKUP_EXPANDED: "miv_ssn_backup_expanded",

    // Login events
    LOGIN_CLICKED: "miv_login_clicked",
    LOGIN_ERROR_DISPLAYED: "miv_login_error_displayed",
    LOGIN_SUCCESS: "miv_login_success",
    
    // Card events
    CARD_DISPLAYED_SUCCESS: "miv_card_displayed_success",
    CARD_DISPLAYED_FAILURE: "miv_card_displayed_failure",
    CARD_DOWNLOAD_CLICKED: "miv_card_download_clicked",
    CARD_DOWNLOAD_SUCCESS: "miv_card_download_success",
    CARD_DOWNLOAD_FAILURE: "miv_card_download_failure",
    CARD_DOWNLOAD_CANCELLED: "miv_card_download_cancelled",
    
    // Menu events
    MENU_LOGOUT_CLICKED: "miv_menu_logout_clicked",
    MENU_ID_CARD_CLICKED: "miv_menu_id_card_clicked",

    // CSML events
    CSML_WIDGET_EXPANDED: "miv_csml_widget_expanded",
    CSML_CTA_CLICKED: "miv_csml_cta_clicked",

    // Reachability events
    REACHABILITY_TILE_EXPANDED: "miv_reachability_tile_expanded",
    CONTACT_INFORMATION_CONFIRM_CLICKED: "miv_contact_information_confirm_clicked",
    CONTACT_INFORMATION_SAVED: "miv_contact_information_saved",
    CONTACT_INFORMATION_SAVE_FAILED: "miv_contact_information_save_failed",
    EMAIL_ADDRESS_ADDED: "miv_email_address_added",
    MOBILE_PHONE_NUMBER_ADDED: "miv_mobile_phone_number_added",
} as const;

type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

/**
 * useAnalytics Hook
 * 
 * @returns Object with trackEvent function
 */
export function useAnalytics() {
    /**
     * Track an analytics event
     * 
     * @param eventName - Name of the event to track
     * @param properties - Additional event properties
     */
    const trackEvent = useCallback(
        async (
            eventName: AnalyticsEventName | string,
            properties: Record<string, any> = {}
        ): Promise<void> => {
            try {
                // Collect browser metadata for Watchtower
                const metadata = collectData();
                
                const eventProperties = {
                    ...properties,
                    timestamp: new Date().toISOString(),
                };

                // Only log in development mode to avoid noise in production
                if (import.meta.env.DEV) {
                    console.log(`[Analytics] Tracking event: ${eventName}`, eventProperties);
                }

                const csrfToken = await getCsrfToken();

                const response = await fetch("/api/analytics/track", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-csrf-token": csrfToken,
                    },
                    credentials: "include", // Include cookies for JWT if present
                    body: JSON.stringify({
                        eventName,
                        properties: eventProperties,
                        meta: metadata, // Include browser/page metadata for Watchtower
                    }),
                });

                if (!response.ok) {
                    if (import.meta.env.DEV) {
                        console.error("[Analytics] Event tracking failed:", {
                            eventName,
                            status: response.status,
                        });
                    }
                } else {
                    if (import.meta.env.DEV) {
                        console.log(`[Analytics] Event tracked successfully: ${eventName}`);
                    }
                }
            } catch (error) {
                // Silently fail - analytics errors should never impact UX
                if (import.meta.env.DEV) {
                    console.error("[Analytics] Error tracking event:", {
                        eventName,
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
            }
        },
        []
    );

    /**
     * Track a page view
     * Captures referrer and page information automatically via Watchtower
     * 
     * @param properties - Optional additional properties
     */
    const trackPageView = useCallback(
        async (properties: Record<string, any> = {}): Promise<void> => {
            try {
                // Collect browser metadata for Watchtower
                const metadata = collectData();
                
                const eventProperties = {
                    ...properties,
                    timestamp: new Date().toISOString(),
                };

                // Only log in development mode to avoid noise in production
                if (import.meta.env.DEV) {
                    console.log("[Analytics] Tracking pageview", eventProperties);
                }

                const csrfToken = await getCsrfToken();

                const response = await fetch("/api/analytics/pageview", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-csrf-token": csrfToken,
                    },
                    credentials: "include", // Include cookies for JWT if present
                    body: JSON.stringify({
                        properties: eventProperties,
                        meta: metadata, // Include browser/page metadata for Watchtower
                    }),
                });

                if (!response.ok) {
                    if (import.meta.env.DEV) {
                        console.error("[Analytics] Pageview tracking failed:", {
                            status: response.status,
                        });
                    }
                } else {
                    if (import.meta.env.DEV) {
                        console.log("[Analytics] Pageview tracked successfully");
                    }
                }
            } catch (error) {
                // Silently fail - analytics errors should never impact UX
                if (import.meta.env.DEV) {
                    console.error("[Analytics] Error tracking pageview:", {
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
            }
        },
        []
    );

    return {
        trackEvent,
        trackPageView,
        AnalyticsEvents,
    };
}

export default useAnalytics;
